import React, {
  ForwardedRef,
  forwardRef,
  memo,
  MutableRefObject,
  PropsWithChildren,
  ReactElement,
  Reducer,
  Ref,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import { useResize } from '../../hooks/useResize';
import { useStickyTableHeader } from '../../hooks/useStickyTableHeader';
import { useUpdateEffect } from '../../hooks/useUpdateEffect';
import { COLUMN_MIN_WIDTH, EXPAND_COLUMN_WIDTH } from './constants';
import { dataTableReducer } from './reducer';
import { DataTableHeader } from './DataTableHeader';
import { DataTableRows } from './DataTableRows';
import { SelectRowAction, SelectRowsAction, DataTableAction, DataTableState, ExpandedRowAction } from './types';
import type { DataTableMode, DataTableProps } from './types';
import { defaultRowKeyExtractor, defaultRowSelectionKeyExtractor, getColGroup, getTotalColumnCount } from './utils';
import { TableContainer } from './TableContainer';
import { Table } from './Table';
import { TableColGroup } from './TableColGroup';
import styled from '@emotion/styled';
import { buildCSSWithLength } from '../../styles';
import { FloatingTableHeader } from './FloatingTableHeader';
import { StickyHeaderHost, StickyHeaderSentinel } from './styles';

export type { DataTableProps } from './types';

export const DataTable = memo(
  forwardRef(
    <DataType extends object>(
      {
        columns,
        rows = [],
        loading = false,
        stickyHeader = true,
        cellHoverStyle = true,
        cellDisabledStyle = true,
        autoResetRowSelection = true,
        resize = false,
        scroll,
        className,
        sortBy,
        sortDirection,
        selectableRows,
        clickableRows,
        hideSelectAll,
        rowSelection,
        noDataText,
        noDataPadding,
        expandableButton = true,
        rowExpansion,
        onExpandRows,
        renderExpandedRow,
        dividerRows,
        rowColor,
        rowKeyExtractor = defaultRowKeyExtractor,
        rowSelectionKeyExtractor = defaultRowSelectionKeyExtractor,
        onSortBy,
        onSelectRows,
        setContainerRef,
        virtualizedProps,
      }: DataTableProps<DataType>,
      ref: ForwardedRef<HTMLTableElement>,
    ) => {
      const [{ allSelected, selectedRows, expandedRows, emitSelectedEvent, emitExpandedEvent }, dispatch] = useReducer<
        Reducer<DataTableState<DataType>, DataTableAction<DataType>>
      >(dataTableReducer({ rowSelectionKeyExtractor }), {
        emitSelectedEvent: true,
        allSelected: false,
        autoResetRowSelection,
        selectedRows: new Map(),
        expandedRows: [],
        emitExpandedEvent: true,
      });
      const [tableContainerAttribute, setTableContainerAttribute] = useState(0);
      const { windowWidth } = useResize(500);
      const tableElementRef = useRef<HTMLTableElement | null>(null);
      const tableContainerElementRef = useRef<HTMLDivElement | null>(null);
      const stickyHeaderSentinelRef = useRef<HTMLDivElement | null>(null);
      const floatingHeaderRef = useRef<HTMLDivElement | null>(null);
      const translateInnerRef = useRef<HTMLDivElement | null>(null);
      const isPageSticky = typeof stickyHeader === 'object' && stickyHeader !== null && stickyHeader.mode === 'page';
      const mode: DataTableMode = isPageSticky ? 'page' : 'container';
      const stickyOffset = isPageSticky ? stickyHeader.offset ?? 0 : 0;
      const containerStickyHeader = stickyHeader === true;
      const totalColumnCount = getTotalColumnCount({
        columns,
        selectableRows,
        expandableButton,
        renderExpandedRow,
      });
      const tableCols = useMemo<Array<{ width?: number; minWidth?: number }>>(
        () => [
          ...(selectableRows ? [{ width: COLUMN_MIN_WIDTH }] : []),
          ...(expandableButton && renderExpandedRow ? [{ width: EXPAND_COLUMN_WIDTH }] : []),
          ...getColGroup({ columns }),
        ],
        [columns, expandableButton, renderExpandedRow, selectableRows],
      );
      const tableCommonProps = {
        columns,
        selectableRows,
        clickableRows,
        expandableButton,
        renderExpandedRow,
        dividerRows,
        hideSelectAll,
        allSelected,
        selectedRows,
        expandedRows,
        resize,
      };
      const tableHeaderProps = {
        sortBy,
        sortDirection,
        stickyHeader: containerStickyHeader,
        stickyOffset: 0,
        onSortBy,
        ...tableCommonProps,
      };
      const tableBodyProps = {
        loading,
        noDataText,
        noDataPadding,
        cellHoverStyle,
        cellDisabledStyle,
        tableContainerAttribute,
        rowColor,
        rowKeyExtractor,
        rowSelectionKeyExtractor,
        ...tableCommonProps,
      };
      const handleSelectedRows = useCallback((action: SelectRowsAction<DataType>) => dispatch(action), []);
      const handleSelectedRow = useCallback((action: SelectRowAction<DataType>) => dispatch(action), []);
      const handleExpandedRows = useCallback((action: ExpandedRowAction<DataType>) => dispatch(action), []);

      const tableRef = useCallback(
        (node: HTMLTableElement | null) => {
          tableElementRef.current = node;
          if (typeof ref === 'function') {
            ref(node);
          } else if (ref) {
            (ref as MutableRefObject<HTMLTableElement | null>).current = node;
          }
        },
        [ref],
      );

      const setContainerRefRef = useRef(setContainerRef);
      useLayoutEffect(() => {
        setContainerRefRef.current = setContainerRef;
      });

      const tableContainerRef = useCallback((node: HTMLDivElement | null) => {
        tableContainerElementRef.current = node;
        setTableContainerAttribute(node?.clientWidth ?? 0);
        setContainerRefRef.current?.(node);
      }, []);

      useEffect(() => {
        setTableContainerAttribute(tableContainerElementRef.current?.clientWidth ?? 0);
      }, [windowWidth]);

      const shouldRenderFloatingHeader = isPageSticky;
      const stickyHeaderMetrics = useStickyTableHeader({
        enabled: shouldRenderFloatingHeader,
        stickyOffset,
        containerRef: tableContainerElementRef,
        tableRef: tableElementRef,
        sentinelRef: stickyHeaderSentinelRef,
        floatingHeaderRef,
        translateInnerRef,
      });

      useEffect(() => {
        if (!rowSelection) return;

        dispatch({
          type: 'SELECT_CONTROLLED_ROW',
          controlledRows: rows
            .map((row, index) => ({ row, index }))
            .filter(({ row, index }) => {
              if (typeof rowSelection === 'function') {
                return rowSelection(row, index);
              } else {
                return rowSelection.includes(rowSelectionKeyExtractor(row, index));
              }
            }),
          rows,
        });
      }, [rowSelection, rows]);

      useUpdateEffect(() => {
        if (selectedRows?.size === 0) {
          return;
        }
        if (autoResetRowSelection) {
          dispatch({
            type: 'RESET_SELECTED_ROWS',
          });
        } else {
          dispatch({
            type: 'CHECK_ALL_SELECTED_ROWS',
            rows,
          });
        }
      }, [rows]);

      useUpdateEffect(() => {
        emitSelectedEvent &&
          selectedRows &&
          onSelectRows?.(
            Array.from(selectedRows?.keys()),
            Array.from(selectedRows?.values()).map((v) => v.row),
          );
      }, [selectedRows]);

      useEffect(() => {
        if (!rowExpansion) return;

        dispatch({
          type: 'EXPAND_CONTROLLED_ROW',
          expandedRows: rows
            .map((row, index) => ({ row, index }))
            .filter(({ row, index }) => {
              if (typeof rowExpansion === 'function') {
                return rowExpansion(row, index);
              } else {
                return rowExpansion.includes(rowSelectionKeyExtractor(row, index));
              }
            }),
          rows,
        });
      }, [rowExpansion, rows]);

      useUpdateEffect(() => {
        if (!emitExpandedEvent || !expandedRows) {
          return;
        }
        onExpandRows?.(expandedRows);
      }, [expandedRows]);

      const tableElement = (
        <StyledTableContainer
          className={className}
          scroll={scroll}
          mode={mode}
          noData={rows.length === 0}
          ref={tableContainerRef}
        >
          <Table className={className} ref={tableRef} scroll={scroll} resize={resize}>
            <TableColGroup cols={tableCols} />
            <thead>
              <DataTableHeader {...tableHeaderProps} onSelectedRows={handleSelectedRows} rows={rows} />
            </thead>
            <tbody>
              {typeof virtualizedProps?.beforePadding === 'number' && (
                <tr>
                  <td colSpan={totalColumnCount} style={{ height: virtualizedProps.beforePadding, padding: 0 }} />
                </tr>
              )}
              <DataTableRows
                {...tableBodyProps}
                onSelectedRow={handleSelectedRow}
                onExpandedRow={handleExpandedRows}
                rows={rows}
                totalColumnCount={totalColumnCount}
                virtualizedItems={virtualizedProps?.virtualizedItems}
              />
              {typeof virtualizedProps?.afterPadding === 'number' && (
                <tr>
                  <td colSpan={totalColumnCount} style={{ height: virtualizedProps.afterPadding, padding: 0 }} />
                </tr>
              )}
            </tbody>
          </Table>
        </StyledTableContainer>
      );

      if (mode !== 'page') {
        return tableElement;
      }

      return (
        <StickyHeaderHost className={classNames('pds-table-sticky-header-host', className)}>
          {shouldRenderFloatingHeader && (
            <>
              <FloatingTableHeader
                {...tableHeaderProps}
                cols={tableCols}
                scroll={scroll}
                rows={rows}
                visible={stickyHeaderMetrics.visible}
                stickyOffset={stickyOffset}
                containerWidth={stickyHeaderMetrics.containerWidth}
                tableWidth={stickyHeaderMetrics.tableWidth}
                headerHeight={stickyHeaderMetrics.headerHeight}
                columnWidths={stickyHeaderMetrics.columnWidths}
                headerRef={floatingHeaderRef}
                translateInnerRef={translateInnerRef}
                onSelectedRows={handleSelectedRows}
              />
              <StickyHeaderSentinel ref={stickyHeaderSentinelRef} className='pds-table-sticky-header-sentinel' />
            </>
          )}
          {tableElement}
        </StickyHeaderHost>
      );
    },
  ),
) as <DataType extends object>(
  props: PropsWithChildren<DataTableProps<DataType>> & { ref?: Ref<HTMLTableElement> },
) => ReactElement;

const StyledTableContainer = styled(TableContainer)<{ noData?: boolean }>`
  ${({ noData }) => noData && `${buildCSSWithLength('max-height', 'auto')};`}
`;
